import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RealtimeProvider } from "@tcg/realtime/react";
import type { i18n as I18n } from "i18next";
import { lazy, Suspense, useEffect, useState, type ReactNode } from "react";
import { I18nextProvider, useTranslation } from "react-i18next";
import { BrowserRouter, MemoryRouter } from "react-router";

import { ApprovalProvider } from "@/app/approval";
import { createServices, ServicesProvider, useAuthState, type PosServices } from "@/app/context";
import { AppRoutes, type AppRoutesProps } from "@/app/router";
import { PortraitHint } from "@/components/PortraitHint";
import { ToastProvider } from "@/components/Toast";
import { PairScreen } from "@/features/auth/PairScreen";
import { PinScreen } from "@/features/auth/PinScreen";
import { i18n as defaultI18n, isPosLocale } from "@/i18n";
import { isApiError } from "@/lib/errors";
import { readSetting } from "@/lib/db";
import { env } from "@/lib/env";
import { posKeyMap } from "@/realtime/keys";

const ComponentGallery = lazy(() => import("@/dev/ComponentGallery"));

export interface AppProps extends AppRoutesProps {
  i18n?: I18n;
  services?: PosServices;
  /** Tests: start at this path with an in-memory router. */
  initialPath?: string;
  queryClient?: QueryClient;
}

function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5_000,
        // an answer that says "no" (4xx) is not retried; network trouble is, once
        retry: (count, error) =>
          count < 1 && !(isApiError(error) && error.status >= 400 && error.status < 500),
        refetchOnWindowFocus: false,
      },
      mutations: { retry: false },
    },
  });
}

/** Who is at the register decides what is shown: pairing → PIN → the screens (+ lock on top). */
function AuthGate({ children }: { children: ReactNode }) {
  const state = useAuthState();
  const { t } = useTranslation();
  if (state.kind === "loading") {
    return (
      <p className="flex h-full items-center justify-center text-xl text-muted-foreground">
        {t("app.loading")}
      </p>
    );
  }
  if (state.kind === "unpaired") {
    return <PairScreen notice={state.notice} />;
  }
  if (state.kind === "locked" && state.reason === "start") {
    return <PinScreen reason="start" lastStaff={state.lastStaff} />;
  }
  return <>{children}</>;
}

export function App({
  i18n = defaultI18n,
  services: given,
  initialPath,
  queryClient: givenClient,
  updates,
  onUpdate,
}: AppProps) {
  const [queryClient] = useState(() => givenClient ?? createQueryClient());
  const [services] = useState(
    () => given ?? createServices({ getLocale: () => i18n.resolvedLanguage ?? "ja" }),
  );

  useEffect(() => {
    if (env.VITE_POS_DEV_TOOLS) {
      // dev / e2e only: the test printer (unplug / plug) — never in a production build
      (window as unknown as { __posDev?: unknown }).__posDev = {
        printer: services.printer.mock,
        labels: services.labels.mock,
        db: services.db,
      };
    }
    void services.auth.init();
    void readSetting<string>(services.db, "locale", "").then((locale) => {
      if (isPosLocale(locale) && locale !== i18n.resolvedLanguage) {
        void i18n.changeLanguage(locale);
      }
    });
  }, [services, i18n]);

  useEffect(() => {
    document.documentElement.lang = i18n.resolvedLanguage ?? "ja";
    const onChange = (lng: string) => {
      document.documentElement.lang = lng;
    };
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, [i18n]);

  const Router = initialPath ? MemoryRouter : BrowserRouter;
  const routerProps = initialPath ? { initialEntries: [initialPath] } : {};
  const devGallery =
    env.VITE_POS_DEV_TOOLS && (initialPath ?? window.location.pathname).startsWith("/_dev/");

  return (
    <I18nextProvider i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        <ServicesProvider value={services}>
          <RealtimeProvider
            client={services.realtime}
            locale={i18n.resolvedLanguage ?? "ja"}
            keyMap={posKeyMap}
          >
            <ToastProvider>
              <ApprovalProvider>
                <Router {...routerProps}>
                  {devGallery ? (
                    <Suspense fallback={null}>
                      <ComponentGallery />
                    </Suspense>
                  ) : (
                    <AuthGate>
                      <AppRoutes
                        {...(updates ? { updates } : {})}
                        {...(onUpdate ? { onUpdate } : {})}
                      />
                    </AuthGate>
                  )}
                </Router>
                <PortraitHint />
              </ApprovalProvider>
            </ToastProvider>
          </RealtimeProvider>
        </ServicesProvider>
      </QueryClientProvider>
    </I18nextProvider>
  );
}
